/**
 * Offline-First Caching Constants
 * Configuration for cache durations, sync settings, and retry logic
 */

export const OFFLINE_CONFIG = {
  // ============== CACHE DURATIONS ==============
  // Master data rarely changes - cache for 7 days
  MASTER_DATA_CACHE_DURATION: 7 * 24 * 60 * 60 * 1000, // 7 days in ms

  // Entity data (cattle, health records, etc.) - cache for 7 days (matches prefetch TTL of 4 hours with safety buffer)
  ENTITY_CACHE_DURATION: 7 * 24 * 60 * 60 * 1000, // 7 days in ms

  // Dashboard data - cache for 30 minutes
  DASHBOARD_CACHE_DURATION: 30 * 60 * 1000, // 30 minutes in ms

  // ============== SYNC SETTINGS ==============
  // Maximum retry attempts for failed operations
  MAX_RETRY_COUNT: 3,

  // Delay between retry attempts (exponential backoff base)
  RETRY_DELAY_MS: 5000, // 5 seconds

  // Delay before auto-sync after coming online
  AUTO_SYNC_DELAY_MS: 2000, // 2 seconds

  // Batch size for processing sync queue
  SYNC_BATCH_SIZE: 10,

  // ============== CONFLICT RESOLUTION ==============
  // Default strategy when server and local data conflict
  DEFAULT_CONFLICT_STRATEGY: 'SERVER_WINS' as const,

  // ============== STORAGE LIMITS ==============
  // Maximum pending operations to store
  MAX_PENDING_OPERATIONS: 1000,

  // Maximum cached entities per type
  MAX_CACHED_ENTITIES: 5000,
} as const;

// Entity types supported for offline operations
export type EntityType =
  | 'cattle'
  | 'healthRecord'
  | 'milkProduction'
  | 'foodHistory'
  | 'medicine'
  | 'inventory'
  | 'stockTransaction'
  | 'shed'
  | 'dungTransaction'
  | 'rfidScan';

// Operation types for sync queue
export type OperationType = 'CREATE' | 'UPDATE' | 'DELETE';

// Sync status for pending operations
export type SyncStatus = 'pending' | 'syncing' | 'failed' | 'completed';

// Conflict resolution strategies
export type ConflictStrategy = 'SERVER_WINS' | 'CLIENT_WINS' | 'MERGE' | 'PROMPT_USER';

// Cache status for entities
export type CacheStatus = 'synced' | 'pending' | 'conflict';

// Master data keys
export const MASTER_DATA_KEYS = [
  'breeds',
  'species',
  'genders',
  'colors',
  'sources',
  'ownerships',
  'feedTypes',
  'inventoryTypes',
  'inventoryUnits',
  'blocks',
  'villages',
  'districts',
  'states',
] as const;

export type MasterDataKey = typeof MASTER_DATA_KEYS[number];
